import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import SearchIcon from '../../assets/icons/search.svg?react';
import type { InterventionRoomResponse } from '../../api/generated';
import { useAppSelector } from '../../store/hooks';
import { selectInterventionRoomProperties } from '../../store/interventionRoomProperties/interventionRoomPropertiesSlice';
import { useInterventionRoomProperties } from '../../hooks/useInterventionRoomProperties';
import { useInterventionRooms } from '../../hooks/useInterventionRooms';
import { FooterCell, Table } from '../UI';
import { DeleteInterventionRoomModal } from './DeleteInterventionRoomModal';
import { getInterventionRoomColumns } from './interventionRoomColumns';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import styles from './InterventionRoomsSettings.module.css';

const NEW_ROOM_ID = 'new' as const;
type EditingRoomId = string | null;

function parseCapacityLimit(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

export const InterventionRoomsSettings = () => {
  const { t } = useTranslation();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = locationIdParam ?? null;

  useInterventionRoomProperties();
  const allRoomProperties = useAppSelector(selectInterventionRoomProperties);
  const { rooms, createRoom, updateRoom, deleteRoom } = useInterventionRooms(locationId);

  const [searchQuery, setSearchQuery] = useState('');
  const [editingRoomId, setEditingRoomId] = useState<EditingRoomId>(null);
  const [editingName, setEditingName] = useState('');
  const [editingCapacityLimit, setEditingCapacityLimit] = useState('');
  const [openPickerRoomId, setOpenPickerRoomId] = useState<string | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<InterventionRoomResponse | null>(null);

  const filteredRooms = useMemo(() => {
    if (!searchQuery) return rooms;
    const q = searchQuery.toLowerCase();
    return rooms.filter((r) => r.description.toLowerCase().includes(q));
  }, [rooms, searchQuery]);

  const effectiveEditingRoomId: EditingRoomId =
    editingRoomId === null || editingRoomId === NEW_ROOM_ID
      ? editingRoomId
      : filteredRooms.some((r) => r.id === editingRoomId)
        ? editingRoomId
        : null;

  const startEditName = useCallback(
    (roomId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      setEditingName(room?.description ?? '');
      setEditingCapacityLimit(room?.capacityLimit?.toString() ?? '');
      setEditingRoomId(roomId);
      setOpenPickerRoomId(null);
    },
    [rooms]
  );

  const cancelEdit = useCallback(() => {
    setEditingRoomId(null);
    setEditingName('');
    setEditingCapacityLimit('');
  }, []);

  const saveName = useCallback(
    async (roomId: string, name: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: name,
        capacityLimit: parseCapacityLimit(editingCapacityLimit),
        interventionRoomPropertyIds: room.interventionRoomProperties.map((rp) => rp.id)
      });
      setEditingRoomId(null);
      setEditingName('');
      setEditingCapacityLimit('');
    },
    [rooms, editingCapacityLimit, updateRoom]
  );

  const handleCreateRoom = useCallback(
    async (name: string) => {
      await createRoom(name);
      setEditingRoomId(null);
    },
    [createRoom]
  );

  const openPicker = useCallback((roomId: string) => {
    setOpenPickerRoomId(roomId);
  }, []);

  const closePicker = useCallback(() => {
    setOpenPickerRoomId(null);
  }, []);

  const addProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: room.description,
        capacityLimit: room.capacityLimit,
        interventionRoomPropertyIds: [
          ...room.interventionRoomProperties.map((rp) => rp.id),
          propertyId
        ]
      });
    },
    [rooms, updateRoom]
  );

  const removeProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: room.description,
        capacityLimit: room.capacityLimit,
        interventionRoomPropertyIds: room.interventionRoomProperties
          .map((rp) => rp.id)
          .filter((id) => id !== propertyId)
      });
    },
    [rooms, updateRoom]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!roomToDelete) return;
    await deleteRoom(roomToDelete.id);
    setRoomToDelete(null);
  }, [roomToDelete, deleteRoom]);

  const columns = useMemo(
    () =>
      getInterventionRoomColumns(t, {
        editingRoomId: effectiveEditingRoomId,
        editingName,
        editingCapacityLimit,
        openPickerRoomId,
        allRoomProperties,
        onStartEditName: startEditName,
        onNameChange: setEditingName,
        onCapacityLimitChange: setEditingCapacityLimit,
        onSaveName: () => {
          if (!effectiveEditingRoomId || effectiveEditingRoomId === NEW_ROOM_ID) return;
          const trimmed = editingName.trim();
          if (trimmed.length === 0) return;
          void saveName(effectiveEditingRoomId, trimmed);
        },
        onCancelEdit: cancelEdit,
        onOpenPicker: openPicker,
        onClosePicker: closePicker,
        onAddProperty: (roomId, propertyId) => {
          void addProperty(roomId, propertyId);
        },
        onRemoveProperty: (roomId, propertyId) => {
          void removeProperty(roomId, propertyId);
        },
        onDelete: (room) => setRoomToDelete(room)
      }),
    [
      t,
      effectiveEditingRoomId,
      editingName,
      editingCapacityLimit,
      openPickerRoomId,
      allRoomProperties,
      startEditName,
      saveName,
      cancelEdit,
      openPicker,
      closePicker,
      addProperty,
      removeProperty
    ]
  );

  return (
    <section>
      <SectionHeader title={t('settings-intervention-rooms-header')}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              {t('settings-search-intervention-room')}
            </label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('intervention-rooms-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={filteredRooms}
        getRowKey={(room) => room.id}
        footer={
          <FooterCell
            isEditing={editingRoomId === NEW_ROOM_ID}
            addLabel={t('add-intervention-room')}
            onStartEdit={() => {
              setEditingRoomId(NEW_ROOM_ID);
              setOpenPickerRoomId(null);
            }}
            onSave={(name) => void handleCreateRoom(name)}
            onCancel={cancelEdit}
          />
        }
      />

      {roomToDelete && (
        <DeleteInterventionRoomModal
          room={roomToDelete}
          onClose={() => setRoomToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </section>
  );
};
